import { Localized, useLocalization } from '@fluent/react'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Fab from '@mui/material/Fab'
import AddIcon from '@mui/icons-material/Add'
import { Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useEffect, useState } from 'react'
import { Box, ButtonGroup } from '@mui/material'
import { Skeleton } from '@mui/material'
import { LoadDeviceTypes } from '../../app/AppDataLoader'
import { DataGrid, GridColDef, } from '@mui/x-data-grid'
import { plPL, enUS } from '@mui/x-data-grid/locales'
import { dynamicUrsFeatureFlag, selectSettings, selectHasFeatureFlag } from '../settings/settingsSlice'
import { UrsHeader } from './UrsSlice'
import { useNavigate } from 'react-router-dom'
import { QuickSearchToolbar } from '../../app/QuickSearchToolbar'
import { getStructureName } from '../device/Structure'
import { LoadingContainer } from '../../app/LoadingContainer'
import { unwrapResult } from '@reduxjs/toolkit'
import { loadDynamicUrsList } from './dynamicUrsSlice'
import { ErrorContainer } from '../../app/ErrorContainer'
import { CreateDynamicUrsDialog } from './CreateDynamicUrsDialog'
import { Package } from '../permissions/usePackage'


export const DynamicUrsList = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();
    const [ursList, setUrsList] = useState<UrsHeader[]>([])

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [createDynamicDialogOpen, setCreateDynamicDialogOpen] = useState(false)

    const hasDynamicUrsFeature = useAppSelector(selectHasFeatureFlag(dynamicUrsFeatureFlag))

    useEffect(() => {
        setIsLoading(true);
        setIsError(false);
        setError(null);

        dispatch(loadDynamicUrsList())
            .then(unwrapResult)
            .then((perms: UrsHeader[]) => {
                setUrsList(perms);
            })
            .catch((err) => {
                console.error(err);
                setIsError(true);
                setError(err);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch]);

    const columns: GridColDef<UrsHeader, any, any>[] = [
        {
            field: 'ursNo',
            headerName: l10n.getString("document-number", null, "_Document number"),
            minWidth: 150
        },
        {
            field: 'version',
            headerName: l10n.getString("document-version", null, "_Document version"),
            valueGetter: (_, row) => {
                return row.version
            }
        },
        {
            field: 'title',
            headerName: l10n.getString("document-title", null, "_Document title"),
            minWidth: 300,
            flex: 1
        },
        {
            field: 'deviceTypeId',
            headerName: l10n.getString("structure", null, "_Structure"),
            width: 300,
            valueGetter: (_, row) => {
                return getStructureName(row.deviceTypeId)
            }
        },
        {
            field: 'createdByFullName',
            headerName: l10n.getString("document-created-by", null, "_Author"),
            width: 200
        },
        {
            field: 'createDate',
            type: 'date',
            headerName: l10n.getString("document-created-date", null, "_Create date"),
            width: 200,
            valueGetter: (_, row) => new Date(row.createDate),
        },
        {
            field: 'status',
            headerName: l10n.getString("document-status", null, "_Status"),
            width: 150,
            valueGetter: (_, row) => {
                var status = row.status.toLowerCase();
                const description = l10n.getString("document-status-" + status, null, "");
                return description;
            }
        },
        {
            field: 'actions', type: 'actions', width: 150,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 75 }}
                            component={Link}
                            to={`/urs/edit/${params.row.id}?structureId=${params.row.deviceTypeId}`}
                            color="secondary"
                            variant="outlined" >
                            <Localized id="edit">
                                <span>Edytuj</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    const handleAddClick = () => {
        
        if (hasDynamicUrsFeature) {
            setCreateDynamicDialogOpen(true)
            return
        }
    }

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const navigate = useNavigate()

    const handleRowDoubleClick = (params) => {
        navigate(`/document/edit/${params.row.id}`)

    };


    if (isLoading) {
        return <LoadingContainer />;
    }

    if (isError) {
        return <ErrorContainer error={error} />;
    }

    return <>
        <LoadDeviceTypes component={<Skeleton variant="rectangular" />}>
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 290px)' }}
                    rows={ursList}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                    onRowDoubleClick={handleRowDoubleClick}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Package name="URS" access="ReadWrite">
                        <Fab sx={{
                            margin: 1,
                        }} onClick={handleAddClick} color="secondary" size="small">
                            <AddIcon />
                        </Fab>
                    </Package>
                </Box>
            </TableContainer>
            {hasDynamicUrsFeature && (
                <CreateDynamicUrsDialog
                    open={createDynamicDialogOpen}
                    onClose={() => setCreateDynamicDialogOpen(false)}
                />
            )}
        </LoadDeviceTypes>
    </>
}
